// Beosztás-lista panel (org-structure „Beosztások" fül) — a KÖZÖS DataTable-re épül. A szervezeti fa LEGALSÓ
// szintje: a beosztás egy CSAPATHOZ tartozik, a részlege pedig a csapatból következik. Szerver-oldali
// szabad-szöveg kereső (300 ms debounce, név/slug), szerver-rendezés (name | slug | teamName |
// departmentName), végtelen görgetés (100/lap). A /org-structure/positions BFF-végpontról fogyaszt
// (workforce.positions busz-erőforrás; a csapat-név/-szín ÉS a csapat részlegének neve feloldva:
// teamId → teamName/teamColor → departmentName).
//
// KÉT FACET-SZŰRŐ van: csapat (finom) és részleg (durva) — a részleg alatt több csapat is lehet, ezért a
// kettő nem helyettesíti egymást. A szín a CSAPATÉ (a részlegnek nincs).
//
// Page-gate: `organizational_structure.view`. CRUD: „+ Új" + ceruza (organizational_structure.manage →
// PositionFormSheet) + kuka (organizational_structure.delete). Törlés-guard: a szerver 409-et ad, ha munkatárs
// hivatkozik a beosztásra → a conflict-ág visszaállít + a „használatban" toastot mutatja (nincs végleges törlés).
import { type OrgPositionRowSchema } from '@atlas/contracts';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { OrgStructureTabs, type OrgTab } from '@/components/org-structure/org-structure-tabs';
import { PositionFormSheet } from '@/components/org-structure/position-form-sheet';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { TeamBadge } from '@/components/shared/team-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { DeleteRowAction, EditRowAction, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { Text } from '@/components/ui/text';
import { positionKeys, teamKeys } from '@/features/org-structure/api';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type PositionRow = z.infer<typeof OrgPositionRowSchema>;
type Member = PositionRow['members'][number];
type ListPage = Awaited<ReturnType<typeof api.orgPositions.query>>;

const PAGE_SIZE = 100;

/** A facet-szűrők „mind" pszeudó-értéke (mint az employees/accounts oldalon). */
const ALL = '__all__';

export function PositionsPanel({
  tab,
  onTabChange,
  deepLinkEditId,
  onClearDeepLink,
}: {
  tab: OrgTab;
  onTabChange: (value: string) => void;
  /** Deep-link: a `?edit=` paraméterből érkező beosztás-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.toLowerCase().startsWith('en');
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('organizational_structure.view');
  const canManage = can('organizational_structure.manage');
  const canDelete = can('organizational_structure.delete');

  const [formTarget, setFormTarget] = useState<{ position: PositionRow | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PositionRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>(ALL);
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);
  const [sort, setSort] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const timer = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const gated = !permsLoading && !canView;
  const filters = { q, sort, order };
  const listKey = positionKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.orgPositions.query({
        query: { q: q || undefined, sort, order, limit: PAGE_SIZE, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.positions : [])),
    [pages],
  );

  // Deep-link: a `?edit=<id>` beosztásra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<PositionRow>({
    id: deepLinkEditId,
    rows,
    getId: (p) => p.id,
    onOpen: (p) => setFormTarget({ position: p }),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));

  // Facet-opciók a betöltött sorok feloldott csapat-/részleg-neveiből (distinct, magyar collation). Ezekre
  // NEM a keresőben, hanem a facet-szűrőkkel lehet szűkíteni (kliens-oldal — a belső beosztás-szám kicsi,
  // egy lap). A RÉSZLEG-facet opcióit szűkíti az aktív csapat-facet és fordítva nem: a részleg a durvább
  // dimenzió, azon belül válogatunk csapatra.
  const collator = useMemo(() => new Intl.Collator('hu', { sensitivity: 'base' }), []);
  const departmentOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.departmentName).filter((v): v is string => !!v))].sort((a, b) =>
        collator.compare(a, b),
      ),
    [rows, collator],
  );
  const teamOptions = useMemo(() => {
    const scoped =
      departmentFilter === ALL ? rows : rows.filter((r) => r.departmentName === departmentFilter);
    return [...new Set(scoped.map((r) => r.teamName).filter((v): v is string => !!v))].sort(
      (a, b) => collator.compare(a, b),
    );
  }, [rows, departmentFilter, collator]);
  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (departmentFilter === ALL || r.departmentName === departmentFilter) &&
          (teamFilter === ALL || r.teamName === teamFilter),
      ),
    [rows, departmentFilter, teamFilter],
  );
  const hasFilters = q !== '' || departmentFilter !== ALL || teamFilter !== ALL;

  const toggleSort = useCallback((col: string) => {
    setSort((prev) => {
      if (prev === col) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setOrder('asc');
      return col;
    });
  }, []);
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista sor-eltávolítás + rollback). A REFERENCIA-GUARD 409-et ad, ha munkatárs hivatkozik a
  // beosztásra — a conflict-ág visszaállít (rollback) + a „használatban" toastot mutatja (nincs végleges törlés).
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.positionDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      // a beosztás eltűnése a CSAPAT-sor `positions[]` listáját is érinti
      invalidateKeys: [positionKeys.lists(), teamKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          if (!old) return old;
          const id = deleteTarget?.id;
          if (!id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      positions: p.body.positions.filter((x) => x.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:org_structure.positions.deleted'),
        conflict: t('toast:org_structure.positions.inUse'),
        forbidden: t('toast:org_structure.positions.forbidden'),
        validation: t('toast:org_structure.positions.validation'),
        error: t('toast:org_structure.positions.error'),
      },
    },
  );

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  const emDash = <span className="text-muted-foreground">—</span>;

  // Egy tag megjelenítendő neve (locale-sorrend, mint a support-teams/munkatársak-oldal).
  const memberName = useCallback(
    (m: Member): string =>
      formatName(
        { givenName: m.givenName, familyName: m.familyName, displayName: m.displayName },
        i18n.language,
      ),
    [i18n.language],
  );

  const columns: DataTableColumn<PositionRow>[] = [
    {
      // 1. Beosztás — a lokalizált név (itemTitle) + a CSAPAT színes badge-e (a slug-alcím helyett; a slug a
      // szerkesztő-modalban látszik). A RÉSZLEG NEVE ITT SZÁNDÉKOSAN NINCS: a badge mellé írva zsúfolt volt,
      // és a részleg amúgy is külön facet-szűrő a toolbarban. Feloldatlan csapat → csak a név.
      id: 'name',
      header: t('orgStructure.positions.columns.name'),
      sortable: true,
      cardLabel: t('orgStructure.positions.columns.name'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      cell: (p) => {
        const name = isEn ? p.nameEn : p.nameHu;
        return (
          <div className="min-w-0 space-y-1">
            <Text variant="itemTitle" as="div" className="truncate">
              {name || p.slug || emDash}
            </Text>
            {p.teamName && (
              <TeamBadge name={p.teamName} color={p.teamColor} className="max-w-full truncate" />
            )}
          </div>
        );
      },
    },
    {
      // 2. Munkatársak — a beosztáshoz rendelt belső munkatársak: profilkép + név; 3+ tagnál legördülő
      // (ExpandableCell, a support-teams „Tagok" oszlop pontos designja).
      id: 'members',
      header: t('orgStructure.columns.employees'),
      cardLabel: t('orgStructure.columns.employees'),
      card: 'subtitle',
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (p) =>
        p.members.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<Member>
            items={p.members}
            getKey={(m) => m.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('orgStructure.moreMembers', { count: n })}
            triggerAriaLabel={t('orgStructure.moreMembersAria')}
            renderItem={(m) => {
              const name = memberName(m);
              return (
                <PersonChip
                  name={name}
                  avatarUrl={m.avatarUrl}
                  accentColor={m.teamColor}
                  size="sm"
                  className="gap-1.5"
                />
              );
            }}
          />
        ),
    },
  ];

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) az AKTÍV fül (Beosztások) create-jét nyitja — csak manage-joggal.
      // Desktopon a toolbar ListNewButton-ja (lásd lent, `hidden md:block`).
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('orgStructure.positions.new'),
              onClick: () => setFormTarget({ position: null }),
            }
          : undefined
      }
      toolbar={
        <div>
          <div className="px-4 pt-2">
            <OrgStructureTabs value={tab} onValueChange={onTabChange} />
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            filters={[
              {
                key: 'department',
                label: t('orgStructure.positions.filterDepartment'),
                value: departmentFilter,
                onChange: (v) => {
                  setDepartmentFilter(v);
                  setTeamFilter(ALL); // részleg-váltáskor a csapat-facet ürül (más részleg más csapatokat kínál)
                },
                options: departmentOptions.map((d) => ({ value: d, label: d })),
                allValue: ALL,
              },
              {
                key: 'team',
                label: t('orgStructure.positions.filterTeam'),
                value: teamFilter,
                onChange: setTeamFilter,
                options: teamOptions.map((tm) => ({ value: tm, label: tm })),
                allValue: ALL,
              },
            ]}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('orgStructure.loading')
                : !isError && !infinite.isLoading
                  ? t('orgStructure.positions.countOf', { shown: filteredRows.length, total })
                  : ''
            }
            actions={
              // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
              <div className="hidden md:block">
                <ListNewButton
                  label={t('orgStructure.positions.new')}
                  onClick={() => setFormTarget({ position: null })}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<PositionRow>
          columns={columns}
          rows={filteredRows}
          getRowId={(p) => p.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('orgStructure.positions.noMatches')}
                description={t('orgStructure.positions.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('orgStructure.positions.empty')}
                description={t('orgStructure.positions.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('orgStructure.columns.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'}
          rowActions={
            canManage || canDelete
              ? (p) => (
                  <RowActions>
                    {canManage && (
                      <EditRowAction
                        label={t('orgStructure.edit')}
                        onClick={() => setFormTarget({ position: p })}
                      />
                    )}
                    {canDelete && (
                      <DeleteRowAction
                        label={t('orgStructure.delete')}
                        onClick={() => setDeleteTarget(p)}
                      />
                    )}
                  </RowActions>
                )
              : undefined
          }
        />
      </div>

      <PositionFormSheet
        open={formTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        position={formTarget?.position ?? null}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('orgStructure.positions.deleteTitle')}
        description={t('orgStructure.positions.deleteDesc', {
          name: deleteTarget ? (isEn ? deleteTarget.nameEn : deleteTarget.nameHu) : '',
        })}
        confirmLabel={t('orgStructure.delete')}
        cancelLabel={t('orgStructure.cancel')}
        pendingLabel={t('orgStructure.deleting')}
        tone="danger"
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
